import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { Button, Input, Space, Table, TablePaginationConfig } from "antd";
import { ColumnType } from "antd/es/table";
import { SearchOutlined, SyncOutlined } from "@ant-design/icons";
import moment from "moment";

import StringUtils from "../../../util/StringUtils";
import SimCardTrafficApi from "../../../api/SimCardTrafficApi";
import { FilterItem, FilterItemLabel, SimCardTrafficManagementWrapper, TableWrapper, ToolBarLeftWrapper, ToolBarRightWrapper, ToolBarWrapper } from "./SimCardTrafficManagement.styled";
import SimCardTrafficDetails from "./components/sim-card-traffic-details/SimCardTrafficDetails";
import SimCardTrafficUtils from "./utils/SimCardTrafficUtils";

interface SimCardTrafficManagementProps {

}

interface TableParams {
    pagination?: TablePaginationConfig;
    sortField?: string;
    sortOrder?: 'ASC' | 'DESC';
}

const SimCardTrafficManagement: React.FC<SimCardTrafficManagementProps> = (props) => {
    let simCardTrafficApi = new SimCardTrafficApi();
    let navigate = useNavigate();

    let [simCardTrafficViews, setSimCardTrafficViews] = useState([]);
    let [loading, setLoading] = useState(true);
    let [search, setSearch] = useState("");
    let [tableParams, setTableParams] = useState<TableParams>({
        sortField: 'iccid',
        sortOrder: 'ASC',
        pagination: {
            current: 1,
            pageSize: 20,
            total: 0,
        },
    });

    let viewSimCardTrafficDetail = (simCardTrafficView: any) => {
        navigate(`./sim-card/${simCardTrafficView.latestSimCardTraffic.iccid}`);
    };

    let onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let searchValue = e.target.value;
        setSearch(searchValue);
        tableParams.pagination.current = 1;
        loadSimCardTrafficViews();
    };

    let onTableChange = (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        let newTableParams: TableParams = {
            pagination: pagination,
            sortField: sorter.field,
            sortOrder: sorter.order,
        };
        setTableParams(newTableParams);
        loadSimCardTrafficViews();
    };

    let loadSimCardTrafficViews = async () => {
        setLoading(true);
        let queryRequest = {
            search: search,
            pageNo: tableParams.pagination.current,
            pageSize: tableParams.pagination.pageSize,
            sortField: tableParams.sortField,
            sortOrder: tableParams.sortOrder,
        };
        let response = await simCardTrafficApi.querySimCardTrafficViews(queryRequest);
        let data = response.data.data;

        let newTableParams = { ...tableParams, pagination: { ...tableParams.pagination } };
        newTableParams.pagination.total = data.totalElements;
        setTableParams(newTableParams);
        setSimCardTrafficViews(data.elements);
        setLoading(false);
    };

    let getUsagePercent = (used: number, total: number) => {
        if (!total || total === 0) return 0;
        return ((used / total) * 100).toFixed(1);
    };

    let columns: ColumnType<any>[] = [
        {
            title: '设备序列号',
            dataIndex: 'tenantNode.sn',
            key: 'tenantNode.sn',
            align: 'center',
            fixed: 'left',
            render: (text: string, record: any) => {
                return <span>{StringUtils.emptyToSlash(record.tenantNode?.sn)}</span>;
            }
        },
        {
            title: 'ICCID',
            dataIndex: 'latestSimCardTraffic.iccid',
            key: 'latestSimCardTraffic.iccid',
            align: 'center',
            fixed: 'left',
            render: (text: string, record: any) => {
                return <a onClick={() => viewSimCardTrafficDetail(record)}>{record.latestSimCardTraffic?.iccid}</a>;
            }
        },
        {
            title: '设备名称',
            dataIndex: 'device.name',
            key: 'device.name',
            align: 'center',
            fixed: 'left',
            render: (text: string, record: any) => {
                return <span>{StringUtils.emptyToSlash(record.device?.name)}</span>;
            }
        },
        {
            title: '产品型号',
            dataIndex: 'product.model',
            key: 'product.model',
            align: 'center',
            render: (text: string, record: any) => {
                return <span>{StringUtils.emptyToSlash(record.product?.model)}</span>;
            }
        },
        {
            title: '租户名称',
            dataIndex: 'tenant.name',
            key: 'tenant.name',
            align: 'center',
            render: (text: string, record: any) => {
                return <span>{StringUtils.emptyToSlash(record.tenant?.name)}</span>;
            }
        },
        {
            title: '流量套餐',
            dataIndex: 'latestSimCardTraffic.iratePlanName',
            key: 'latestSimCardTraffic.iratePlanName',
            align: 'center',
            render: (text: string, record: any) => {
                return <span>{StringUtils.emptyToSlash(record.latestSimCardTraffic?.iratePlanName)}</span>;
            }
        },
        {
            title: '已使用流量(MB)',
            dataIndex: 'latestSimCardTraffic.used',
            key: 'latestSimCardTraffic.used',
            align: 'center',
            sorter: true,
            render: (text: number, record: any) => {
                let used = record.latestSimCardTraffic?.used || 0;
                let total = record.latestSimCardTraffic?.total || 0;
                return <span>{used.toFixed(1)} ({getUsagePercent(used, total)}%)</span>;
            }
        },
        {
            title: '总流量(MB)',
            dataIndex: 'latestSimCardTraffic.total',
            key: 'latestSimCardTraffic.total',
            align: 'center',
            render: (text: number, record: any) => {
                return <span>{record.latestSimCardTraffic?.total || 0}</span>;
            }
        },
        {
            title: '状态',
            dataIndex: 'latestSimCardTraffic.status',
            key: 'latestSimCardTraffic.status',
            align: 'center',
            sorter: true,
            render: (text: string, record: any) => {
                return <span>{SimCardTrafficUtils.getStatusText(record.latestSimCardTraffic?.status)}</span>;
            }
        },
        {
            title: '过期时间',
            dataIndex: 'latestSimCardTraffic.expireDate',
            key: 'latestSimCardTraffic.expireDate',
            align: 'center',
            sorter: true,
            render: (text: number, record: any) => {
                let expireDate = record.latestSimCardTraffic?.expireDate;
                if (!expireDate) return '-';
                return <span>{moment(parseInt(expireDate)).format("YYYY-MM-DD HH:mm:ss")}</span>;
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            fixed: 'right',
            render: (text: any, record: any) => {
                return <Space size="middle">
                    <a onClick={() => viewSimCardTrafficDetail(record)}>查看详情</a>
                </Space>;
            }
        }
    ];

    useEffect(() => {
        loadSimCardTrafficViews();
    }, []);

    return (
        <SimCardTrafficManagementWrapper>
            <ToolBarWrapper>
                <ToolBarLeftWrapper>
                    <Space size="middle">
                        <Button type="primary" size="middle" icon={<SyncOutlined />} onClick={() => simCardTrafficApi.sync()}>同步流量</Button>
                    </Space>
                </ToolBarLeftWrapper>

                <ToolBarRightWrapper>
                    <FilterItem>
                        <Input
                            placeholder="请输入关键字"
                            suffix={<SearchOutlined />}
                            size="middle"
                            value={search}
                            onChange={onSearchChange}
                        />
                    </FilterItem>
                </ToolBarRightWrapper>
            </ToolBarWrapper>
            <TableWrapper>
                <Table
                    rowKey={(record) => record.latestSimCardTraffic?.iccid}
                    columns={columns}
                    dataSource={simCardTrafficViews}
                    pagination={tableParams.pagination}
                    loading={loading}
                    scroll={{x: true}}
                    onChange={onTableChange}
                />
            </TableWrapper>

            <Routes>
                <Route path="sim-card/:iccid" element={<SimCardTrafficDetails />} />
            </Routes>
        </SimCardTrafficManagementWrapper>
    );
}

export default SimCardTrafficManagement;